import React from 'react'
import { Box } from '@mui/material'
import { Alert, AlertTitle } from '@mui/material';

/**
 * @typedef {object} InfoBoxProps
 * @property {'error' | 'warning' | 'info' | 'success'} severity - El tipo de alerta que determina el color y el icono.
 * @property {string} title - El título de la alerta.
 * @property {React.ReactNode} message - El contenido del mensaje de la alerta.
 * @property {boolean} [hidden=false] - Si es `true`, el componente no se renderiza.
 * @property {object} [style] - Estilos CSS personalizados a aplicar al componente `Alert`.
 * @property {React.ReactNode} [action=null] - Contenido adicional a renderizar en la parte inferior derecha de la alerta (ej. botones de acción).
 * @property {boolean} [closable=false] - Si es `true`, muestra un botón de cierre en la alerta.
 * @property {function(): void} [onClose=null] - La función callback que se ejecuta cuando se hace clic en el botón de cierre. Requerido si `closable` es `true`.
 */

/**
 * Un componente de caja de información que renderiza una alerta de Material-UI con título, mensaje y acciones opcionales.
 * 
 * Proporciona una forma consistente de mostrar mensajes de error, advertencia, información o éxito
 * al usuario. Soporta contenido personalizado, acciones y la capacidad de ser cerrado por el usuario.
 * 
 * @component
 * @example
 * const MyPage = () => {
 *   const [showAlert, setShowAlert] = useState(true);
 * 
 *   return (
 *     <InfoBox
 *       severity="warning"
 *       title="Atención"
 *       message="Esta acción no se puede deshacer."
 *       closable
 *       onClose={() => setShowAlert(false)}
 *       hidden={!showAlert}
 *       action={
 *         <button onClick={handleConfirm}>Confirmar</button>
 *       }
 *     />
 *   );
 * };
 * 
 * @example
 * // Alerta de éxito simple
 * <InfoBox
 *   severity="success"
 *   title="Éxito"
 *   message="La operación se completó correctamente."
 * />
 * 
 * @param {InfoBoxProps} props - Las props del componente InfoBox.
 */
export const InfoBox = ({
  severity,
  title,
  message,
  hidden = false,
  style,
  action = null,
  closable = false,
  onClose = null,
}) => {
  if (hidden) return null
  return (
    <Alert severity={severity} style={style ? style : null} onClose={closable && onClose}>
      <AlertTitle>{title}</AlertTitle>
      <Box display="flex" flexDirection="column">
        {message}
        <Box display="flex" justifyContent="flex-end" width="100%">
          {action}
        </Box>
      </Box>
    </Alert>
  )
}
